<template>
    <div class="form-checkbox" :class="{'is-active': isActive, 'is-disabled': props.disabled, 'is-required': isRequired || (props.rules && props.rules.accepted), 'is-indeterminate': props.indeterminate, 'has-error': hasError, 'cursor-pointer': !props.disabled, 'pb-12': !props.hideDetails}">
        <label class="form-checkbox__content" :class="{'items-center': props.center}">
           <span class="form-checkbox__box" :class="{'mr-12': props.label || $slots.default}" :style="{borderColor: props.color, backgroundColor: isActive ? props.color : null}">
                <input type="checkbox" ref="inputElement" v-model="submitValue" :value="props.trueValue" :true-value="props.trueValue" :false-value="props.falseValue" :disabled="props.disabled" @change="emit('change')">
                <span class="checkmark">
                </span>
           </span>
            <span class="flex flex-column relative justify-center overflow-hidden flex-grow-x-1">
                <span v-if="props.label" class="form-checkbox__label" :class="{'truncate': props.oneLine}" v-html="props.label"/>
                <slot/>
            </span>
        </Label>
        <div v-if="!props.hideDetails" class="form-checkbox__details">
            <span v-if="hasError && errorMessage" class="form-checkbox__message">{{ errorMessage }}</span>
            <span v-else-if="props.help" class="form-checkbox__message">{{ props.help }}</span>
        </div>
    </div>
</template>

<script setup lang="ts">

import {ToggleInputComponentEmits, type ToggleInputComponentProps, useToggleInputComponent} from "../../../../composables/useToggleInputComponent";
import {ref} from "vue";
import type {Ref} from "vue";

const props = withDefaults(defineProps<{
	indeterminate?: boolean,

	// Whether to display the checkbox label in one line and truncate overflowing text
	oneLine?: boolean,

	center?: boolean,

	color?: string
} & ToggleInputComponentProps>(), {
	trueValue: true,
	falseValue: false,
});

const emit = defineEmits(ToggleInputComponentEmits);

const inputElement: Ref<HTMLElement | null> = ref(null);

const {submitValue, isActive, isRequired, hasError, errorMessage, toggle} = useToggleInputComponent(inputElement, props, emit);

defineExpose({
	toggle,
});

</script>

<style>
/*********************
  Inputs
**********************/
/*********************
  Typography
**********************/
/*********************
  Inputs
**********************/
/*********************
  Shadows
**********************/
/*********************
  Layout
**********************/
/*********************
  Card
**********************/
/*********************
  Opacity
**********************/
.form-checkbox,
.form-checkbox * {
  -webkit-tap-highlight-color: transparent;
  user-select: none;
}

.form-checkbox {
  width: 100%;
  position: relative;
  display: flex;
  flex-direction: column;
}
.form-checkbox .form-checkbox__content {
  display: flex;
  cursor: pointer;
  line-height: 1.4;
}
.form-checkbox .form-checkbox__label {
  font-size: 14px;
  line-height: 1.2;
  display: inline-block;
  color: #465050;
}
.form-checkbox.is-required .form-checkbox__label:after {
  content: " *";
  color: #FA5555;
}
.form-checkbox .form-checkbox__box {
  display: block;
  position: relative;
  width: 18px;
  max-width: 18px;
  min-width: 18px;
  height: 18px;
  background-color: transparent;
  border: 2px solid #465050;
  border-radius: 2px;
  transition: background 0.3s ease;
  vertical-align: middle;
  cursor: pointer;
}
.form-checkbox .form-checkbox__box input[type=checkbox] {
  opacity: 0;
  width: 0;
  height: 0;
}
.form-checkbox .form-checkbox__box .checkmark {
  position: absolute;
  top: 0;
  left: 0;
  height: 100%;
  width: 100%;
  background-color: transparent;
  display: flex;
  align-items: center;
  justify-content: center;
}
.form-checkbox .form-checkbox__box .checkmark:after {
  content: "";
  position: absolute;
  display: none;
  left: 5px;
  top: 0;
  width: 5px;
  height: 12px;
  border: solid white;
  border-width: 0 2px 2px 0;
  transform: rotate(45deg);
}
.form-checkbox .form-checkbox__box .form-checkbox__text {
  font-size: 14px;
}
.form-checkbox .form-checkbox__box .form-checkbox__off {
  margin-right: 8px;
}
.form-checkbox .form-checkbox__box .form-checkbox__on {
  margin-left: 8px;
  color: #7D8484;
}
.form-checkbox.is-active .form-checkbox__box {
  background-color: #47a447;
  border-color: #47a447;
}
.form-checkbox.is-active .form-checkbox__box .checkmark:after {
  display: block;
}
.form-checkbox.is-disabled .form-checkbox__content {
  cursor: not-allowed;
}
.form-checkbox.is-disabled .form-checkbox__box {
  opacity: 0.7;
  cursor: not-allowed;
}
.form-checkbox.is-indeterminate .form-checkbox__box {
  background-color: #47a447;
  border-color: #47a447;
}
.form-checkbox.is-indeterminate .form-checkbox__box .checkmark:after {
  display: block;
  width: 80%;
  height: 2px;
  background-color: #ffffff;
  border: none;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
}
.form-checkbox .form-checkbox__details {
  display: flex;
  margin-left: 30px;
}
.form-checkbox .form-checkbox__details > .form-checkbox__message {
  font-size: 12px;
  color: #5e6b6b;
  line-height: 1.2;
}
.form-checkbox.has-error .form-checkbox__box {
  border-color: #FA5555;
}
.form-checkbox.has-error .form-checkbox__message {
  color: #FA5555;
}
</style>